import {
    HalfFloatType,
    LinearFilter,
    Material,
    Mesh,
    MeshBasicMaterial,
    OrthographicCamera,
    PlaneGeometry,
    RGBAFormat,
    Scene,
    ShaderMaterial,
    Texture,
    Vector2,
    Vector3,
    WebGLRenderer,
    WebGLRenderTarget
} from "three";
import {LayerContainer} from "features/apartmentViewer/ApartmentViewer/containers/LayerContainer";
import {EffectComposer, ShaderPass} from "three-stdlib";
import {ExposureManager} from "features/apartmentViewer/ApartmentViewer/services/ExposureManager";
import {CameraHandler} from "features/apartmentViewer/ApartmentViewer/services/CameraHandler";
import {CanvasService} from "features/apartmentViewer/ApartmentViewer/services/CanvasService";
import {TimeService} from "features/apartmentViewer/ApartmentViewer/services/TimeService";
import {BaseMaterialFactory} from "features/apartmentViewer/ApartmentViewer/shaders/baseMaterial/BaseMaterialFactory";
import {
    ExposureMaterialFactory
} from "features/apartmentViewer/ApartmentViewer/shaders/exposureMaterial/ExposureMaterialFactory";
import {
    SharpenMaterialFactory
} from "features/apartmentViewer/ApartmentViewer/shaders/sharpenMaterial/SharpenMaterialFactory";
import {LayerManager} from "features/apartmentViewer/ApartmentViewer/services/LayerManager";

export class PostProcessingRenderer {
    private scene: Scene;
    private plane: Mesh<PlaneGeometry, Material>;

    private baseMaterial: ShaderMaterial;
    private renderTarget: WebGLRenderTarget;
    private finalComposer: EffectComposer | undefined;
    private exposurePass: ShaderPass | undefined;
    private sharpenPass: ShaderPass | undefined;

    private rgbValues = [
        new Vector3(1,.26,0),
        new Vector3(1,.53,.05),
        new Vector3(1,.69,.43),
        new Vector3(1,.8,.65),
        new Vector3(1,.89,.8),
        new Vector3(1,.96,.93),
        new Vector3(.95,.95,1),
        new Vector3(.87,.9,1),
        new Vector3(.82,.87,1),
        new Vector3(.79,.85,1),
        new Vector3(.76,.84,1),
        new Vector3(.75,.83,1)
    ];

    constructor (
        private timeService: TimeService,
        private cameraHandler: CameraHandler,
        private renderer: WebGLRenderer,
        private canvasService: CanvasService,
        private exposureManager: ExposureManager,
        private layerManager: LayerManager,
        private readonly camera: OrthographicCamera = new OrthographicCamera(-1, 1, 1, -1, 1, 100),
    ) {
        this.initializeScene();
    }

    private initializeScene() {
        this.scene = new Scene();
        this.initializeCamera();
        this.initializePlane();
    }

    private initializeCamera() {
        this.camera.position.z = 1;
    }

    private initializePlane() {
        const planeGeometry = new PlaneGeometry(2, 2);
        const material = new MeshBasicMaterial({ color: 0x00FF00 });
        this.plane = new Mesh(planeGeometry, material);
        this.scene.add(this.plane);
    }

    setFinalPostProcessing(postProcessPass: LayerContainer) {
        this.initializeEffectComposer();
        this.addExposurePass(postProcessPass);
        this.addSharpenPass();
        this.setSize();
    }

    private initializeEffectComposer() {
        this.finalComposer = new EffectComposer(this.renderer);
    }

    private addExposurePass(postProcessPass: LayerContainer) {
        if (this.exposurePass) {
            this.exposurePass.dispose();
        }

        if(!this.finalComposer) return;

        this.exposurePass = new ShaderPass(
            ExposureMaterialFactory.createExposureShader(postProcessPass.getActiveLayersCount())
        );
        this.finalComposer.addPass(this.exposurePass);
    }

    private addSharpenPass() {
        if (this.sharpenPass) {
            this.sharpenPass.dispose();
        }

        if(!this.finalComposer) return;

        this.sharpenPass = new ShaderPass(SharpenMaterialFactory.createMaterial());
        this.finalComposer.addPass(this.sharpenPass);
    }


    setSize() {
        if(this.sharpenPass) {
            this.sharpenPass.uniforms.resolution.value = new Vector2(this.canvasService.width, this.canvasService.height);
            this.sharpenPass.uniforms.amount.value = Math.min(.6, this.canvasService.height / 1080);
        }

        if(!this.finalComposer) return;


        this.renderer.setSize(this.canvasService.width, this.canvasService.height);
        this.finalComposer.setSize(this.canvasService.width, this.canvasService.height);

        if(this.renderTarget) {
            this.renderTarget.dispose();
        }

        this.renderTarget = new WebGLRenderTarget(this.canvasService.width, this.canvasService.height, {
            minFilter: LinearFilter,
            magFilter: LinearFilter,
            format: RGBAFormat,
            type: HalfFloatType
        });
    }

    updateLayers(layerData: LayerContainer, material: ShaderMaterial): void {
        const activeLayerIndex = this.layerManager.updateActiveLayers(layerData);
        this.layerManager.updateMaterialUniforms(material, layerData, activeLayerIndex);
    }

    private updateBaseMaterialTextures(currentTexture: Texture | null, nextTexture: Texture | null) {
        if (this.baseMaterial.uniforms.panoramaCurrent.value !== currentTexture) {
            this.baseMaterial.uniforms.panoramaCurrent.value = currentTexture;
        }
        if (this.baseMaterial.uniforms.panoramaNext.value !== nextTexture) {
            this.baseMaterial.uniforms.panoramaNext.value = nextTexture;
        }
    }

    setPanoramaUniforms(
        currentTexture: Texture | null,
        nextTexture: Texture | null,
        transitionPercentage: number,
        currentAlpha: number,
        nextAlpha: number,
        isTransition: boolean,
    ) {
        if (this.baseMaterial) {
            this.updateBaseMaterialTextures(currentTexture, nextTexture);
            this.updateBaseMaterialTransitionParameters(transitionPercentage, isTransition);
            this.updateBaseMaterialAlpha(currentAlpha, nextAlpha);
            this.updateBaseMaterialCameraProperties();
        }
    }

    private updateBaseMaterialTransitionParameters(transitionPercentage: number, isTransition: boolean) {
        this.baseMaterial.uniforms.u_transitionPercentage.value = transitionPercentage;
        this.baseMaterial.uniforms.u_isPanoramaTransition.value = isTransition;
    }

    private updateBaseMaterialAlpha(currentAlpha: number, nextAlpha: number) {
        this.baseMaterial.uniforms.u_panoramaCurrentAlpha.value = currentAlpha;
        this.baseMaterial.uniforms.u_panoramaNextAlpha.value = nextAlpha;
    }

    private lastCameraRotation = new Vector2();
    private lastFov: number = 0;
    private lastAspectRatio: number = 0;

    private updateBaseMaterialCameraProperties() {
        const cameraRotation = this.cameraHandler.camera.rotation;
        const fovy = this.cameraHandler.camera.fov;
        const aspectRatio = this.canvasService.width / this.canvasService.height;

        if (!this.lastCameraRotation.equals(cameraRotation) || this.lastFov !== fovy || this.lastAspectRatio !== aspectRatio) {
            this.baseMaterial.uniforms.u_rotation.value.set(-cameraRotation.x, -cameraRotation.y);
            this.baseMaterial.uniforms.u_fovy.value = fovy;
            this.baseMaterial.uniforms.u_aspectratio.value = aspectRatio;

            this.lastCameraRotation.set(cameraRotation.x, cameraRotation.y);
            this.lastFov = fovy;
            this.lastAspectRatio = aspectRatio;
        }
    }

    private tempColor: Vector3 = new Vector3();

    getKelvinRGB(temperature: number) {
        const step = 1000; // Temperature step size
        const lowerIndex = Math.floor((temperature - 1000) / step);
        const upperIndex = lowerIndex + 1;

        if (lowerIndex < 0) {
            return this.tempColor.copy(this.rgbValues[0]);
        }

        if (upperIndex >= this.rgbValues.length) {
            return this.tempColor.copy(this.rgbValues[this.rgbValues.length - 1]);
        }

        const ratio = (temperature - (lowerIndex * step + 1000)) / step;
        return this.tempColor.copy(this.rgbValues[lowerIndex]).lerp(this.rgbValues[upperIndex], ratio);
    }

    render(basePass: LayerContainer, postProcessPass: LayerContainer): void {
        // If the base pass requires a material update, recreate the base material
        if (basePass.needsUpdate) {
            // Dispose of the old base material if it exists
            if (this.baseMaterial) {
                this.baseMaterial.dispose();
            }

            // Create a new base material based on the active layer count and HDR settings
            this.baseMaterial = BaseMaterialFactory.createMaterial(this.canvasService.width, this.canvasService.height, basePass.getActiveLayersCount());

            // Set the flag to false since the material has been updated
            basePass.needsUpdate = false;
        }

        this.updateLayers(basePass, this.baseMaterial);
        this.plane.material = this.baseMaterial;

        if(postProcessPass.needsUpdate) {
            this.setFinalPostProcessing(postProcessPass);
            postProcessPass.needsUpdate = false;
        }
        if(!this.exposurePass || !this.finalComposer) return;

        this.updateLayers(postProcessPass, this.exposurePass.material);

        // Set the render target and render the scene
        this.renderer.setRenderTarget(this.renderTarget);
        this.renderer.render(this.scene, this.camera);


        const calculatedExposure = this.exposureManager.getSmoothExposure(this.renderTarget.texture);
        this.exposurePass.material.uniforms.u_exposure.value += 0.06 * (calculatedExposure - this.exposurePass.material.uniforms.u_exposure.value);
        this.exposurePass.material.uniforms.inputTexture.value = this.renderTarget.texture;


        const whitePoint = this.getKelvinRGB(6500);
        this.exposurePass.material.uniforms.u_whitePoint.value.set(whitePoint.x, whitePoint.y, whitePoint.z);


        this.finalComposer.render(this.timeService.deltaTime);
    }
}
